import React, { useState, useEffect } from 'react';
import { CouponAssignmentStats } from '../components/CouponAssignmentStats';
import axios from 'axios';
// lucide-react imports removed due to React 19 type incompatibility
import {
  Card,
  CardContent,
} from '../components/ui/card';
import { Button } from '../components/ui/button';
import { buildAdminOAuthConfig, getAdminAuthServerUrl } from '../auth/adminOAuth';
import { Input } from '../components/ui/input';
import { Badge } from '../components/ui/badge';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import { AuthProvider, useAuth, loginWithRedirect } from '@edusense/shared-auth-core';
import { useAdminApplicationScope } from '../apps/AdminApplicationScope';

interface CouponData {
  id: number;
  code: string;
  type: string;
  status: string;
  targetSystem: string;
  targetPlan: string | null;
  trialDays: number | null;
  discountValue: number | null;
  maxUses: number;
  currentUses: number;
  expiresAt: string | null;
  createdAt: string | null;
  recentUsages?: CouponUsageData[];
}

interface CouponUsageData {
  couponCode: string;
  couponType: string;
  userId: string;
  usedAt: string | null;
  username?: string | null;
  displayName?: string | null;
  email?: string | null;
}

const CouponPage: React.FC = () => {
  const { token: accessToken, isAuthenticated: isLoggedIn } = useAuth();
  const { application, applicationId } = useAdminApplicationScope();
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState<{type: 'success' | 'error', text: string} | null>(null);
  const [coupons, setCoupons] = useState<CouponData[]>([]);
  const [totalCount, setTotalCount] = useState(0);

  const [formData, setFormData] = useState({
    couponType: 'TRIAL_PLAN',
    code: '',
    targetSystem: application.couponTargetSystem,
    targetPlan: application.defaultPlanCode,
    trialDays: '30',
    discountValue: '',
    maxUses: '100',
    validityDays: '30'
  });

  const [issuedCoupon, setIssuedCoupon] = useState<any>(null);

  const couponTypes = [
    { key: 'TRIAL_PLAN', label: '무료 체험 (플랜 N일 부여)' },
    { key: 'DISCOUNT_AMOUNT', label: '정액 할인 (원)' },
    { key: 'DISCOUNT_PERCENT', label: '정률 할인 (%)' },
  ];

  const getAuthHeaders = () => {
    const headers: Record<string, string> = accessToken ? { Authorization: `Bearer ${accessToken}` } : {};
    const xsrfCookie = document.cookie.split('; ').find(row => row.startsWith('XSRF-TOKEN='));
    if (xsrfCookie) {
      headers['X-XSRF-TOKEN'] = xsrfCookie.split('=')[1];
    }
    return headers;
  };

  const loadCoupons = async () => {
    try {
      const response = await axios.get('/api/v1/admin/coupons', {
        params: { applicationId },
        headers: getAuthHeaders(),
        withCredentials: true,
      });
      setCoupons(response.data.coupons || []);
      setTotalCount(response.data.totalCount || 0);
    } catch (error) {
      console.error('쿠폰 목록 로딩 실패:', error);
    }
  };

  useEffect(() => {
    setFormData((current) => ({
      ...current,
      targetSystem: application.couponTargetSystem,
      targetPlan: application.defaultPlanCode,
    }));
    setIssuedCoupon(null);
    setMessage(null);
    if (accessToken) void loadCoupons();
  }, [accessToken, application.couponTargetSystem, application.defaultPlanCode, applicationId]);

  const generateCode = () => {
    const chars = 'ABCDEFGHJKLMNPQRSTUVWXYZ23456789';
    let code = '';
    for (let i = 0; i < 8; i++) code += chars.charAt(Math.floor(Math.random() * chars.length));
    setFormData({ ...formData, code });
  };

  const handleIssueCoupon = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setIssuedCoupon(null);
    setMessage(null);

    const code = formData.code.trim().toUpperCase();
    if (!code) {
      setMessage({ type: 'error', text: '쿠폰 코드를 입력하거나 자동 생성해주세요.' });
      setLoading(false);
      return;
    }

    const expiresAt = new Date();
    expiresAt.setDate(expiresAt.getDate() + (parseInt(formData.validityDays, 10) || 30));

    try {
      const response = await axios.post('/api/v1/admin/coupons', {
        code,
        type: formData.couponType,
        targetSystem: application.couponTargetSystem,
        targetPlan: formData.couponType === 'TRIAL_PLAN' ? formData.targetPlan : null,
        trialDays: formData.couponType === 'TRIAL_PLAN' ? parseInt(formData.trialDays, 10) : null,
        discountValue: formData.couponType !== 'TRIAL_PLAN' ? parseInt(formData.discountValue, 10) : null,
        maxUses: parseInt(formData.maxUses, 10) || 100,
        expiresAt: expiresAt.toISOString(),
      }, { headers: getAuthHeaders() });

      setIssuedCoupon({ ...response.data, code });
      setMessage({ type: 'success', text: '쿠폰이 성공적으로 발급되었습니다.' });
      loadCoupons();
    } catch (error: any) {
      setMessage({ type: 'error', text: error.response?.data?.message || '쿠폰 발급에 실패했습니다.' });
    } finally {
      setLoading(false);
    }
  };

  const copyToClipboard = (text: string) => {
    navigator.clipboard.writeText(text);
    setMessage({ type: 'success', text: '쿠폰 코드가 복사되었습니다.' });
  };

  const statusColor = (status: string) => {
    switch (status) {
      case 'ACTIVE': return 'success';
      case 'EXPIRED': return 'warning';
      case 'DEPLETED': return 'destructive';
      case 'INACTIVE': return 'secondary';
      default: return 'secondary';
    }
  };

  const typeLabel = (type: string) => {
    switch (type) {
      case 'TRIAL_PLAN': return '무료 체험';
      case 'DISCOUNT_AMOUNT': return '정액 할인';
      case 'DISCOUNT_PERCENT': return '정률 할인';
      default: return type;
    }
  };

  const isDiscountType = formData.couponType !== 'TRIAL_PLAN';
  const selectClasses = "flex h-10 w-full items-center justify-between rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50";

  const formatUsageDate = (usedAt: string | null) => {
    if (!usedAt) return '';
    return new Date(usedAt).toLocaleDateString();
  };

  const usageLabel = (usage: CouponUsageData) => {
    return usage.username || usage.displayName || usage.email || usage.userId;
  };

  const renderUsagePreview = (coupon: CouponData) => {
    const usages = coupon.recentUsages || [];

    if (usages.length === 0) {
      return <span className="text-muted-foreground">-</span>;
    }

    const remainingCount = Math.max(coupon.currentUses - usages.length, 0);

    return (
      <div className="space-y-1">
        {usages.map((usage) => (
          <div key={`${coupon.id}-${usage.userId}-${usage.usedAt || 'unknown'}`} className="text-xs leading-tight">
            <div className="font-mono text-foreground break-all">{usageLabel(usage)}</div>
            {usage.usedAt && (
              <div className="text-muted-foreground">{formatUsageDate(usage.usedAt)}</div>
            )}
          </div>
        ))}
        {remainingCount > 0 && (
          <div className="text-xs text-muted-foreground">외 {remainingCount}명</div>
        )}
      </div>
    );
  };

  return (
    <div className="max-w-6xl mx-auto space-y-6">
      <div>
        <h1 className="text-3xl font-bold text-gray-900 mb-2">{application.label} 쿠폰 관리</h1>
        <p className="text-gray-600">{application.packageName} 전용 쿠폰을 발급하고 관리합니다. (총 {totalCount}개)</p>
      </div>

      {/* Issuance is only half the story; this is the half that says whether it worked. */}
      <CouponAssignmentStats applicationId={applicationId} />

      {!application.capabilities.couponRedemption ? (
        <div className="rounded-lg border border-amber-200 bg-amber-50/70 p-4 text-sm text-amber-950">
          {application.label} 앱에는 아직 쿠폰 조회·사용 호출이 연결되지 않았습니다. 기존 발급 기록은 앱별로
          분리해 볼 수 있지만, 사용 불가능한 쿠폰이 생기지 않도록 신규 발급은 막아두었습니다.
        </div>
      ) : null}

      {!isLoggedIn && (
        <div className="p-4 rounded-lg bg-amber-50 border border-amber-200 flex items-center justify-between">
          <span className="text-amber-800 font-medium">쿠폰 발급 및 관리를 위해 로그인이 필요합니다.</span>
          <Button size="sm" onClick={() => {
            loginWithRedirect('internal', buildAdminOAuthConfig({ authServerUrl: getAdminAuthServerUrl() }));
          }}>
            로그인
          </Button>
        </div>
      )}

      {message && (
        <div className={`p-4 rounded-lg flex items-center gap-3 ${
          message.type === 'success' ? 'bg-green-50 text-green-800 border border-green-200' : 'bg-red-50 text-red-800 border border-red-200'
        }`}>
          <span className="font-medium">{message.text}</span>
        </div>
      )}

      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <Card>
          <CardContent className="p-6">
            <h3 className="text-xl font-bold mb-4">새 쿠폰 발급</h3>
            <form onSubmit={handleIssueCoupon} className="space-y-4">
              <div className="space-y-1.5">
                <label className="text-sm font-medium text-foreground">쿠폰 유형 <span className="text-destructive">*</span></label>
                <select
                  className={selectClasses}
                  value={formData.couponType}
                  onChange={(e) => setFormData({...formData, couponType: e.target.value})}
                  required
                >
                  {couponTypes.map((type) => (
                    <option key={type.key} value={type.key}>{type.label}</option>
                  ))}
                </select>
              </div>

              <div className="flex gap-2 items-end">
                <div className="space-y-1.5 flex-1">
                  <label className="text-sm font-medium text-foreground">쿠폰 코드 <span className="text-destructive">*</span></label>
                  <Input
                    placeholder="예: WELCOME2025"
                    value={formData.code}
                    onChange={(e) => setFormData({...formData, code: e.target.value.toUpperCase()})}
                    required
                  />
                </div>
                <Button type="button" variant="secondary" onClick={generateCode}>
                  자동 생성
                </Button>
              </div>

              <div className="space-y-1.5">
                <label className="text-sm font-medium text-foreground">대상 앱</label>
                <div className="rounded-md border bg-muted/40 px-3 py-2">
                  <div className="text-sm font-medium">{application.label}</div>
                  <div className="font-mono text-xs text-muted-foreground">{application.packageName}</div>
                </div>
                <p className="text-xs text-muted-foreground">상단 앱 전환과 동일한 범위로 고정됩니다.</p>
              </div>

              {!isDiscountType && (
                <>
                  <div className="space-y-1.5">
                    <label className="text-sm font-medium text-foreground">대상 플랜</label>
                    <select
                      className={selectClasses}
                      value={formData.targetPlan}
                      onChange={(e) => setFormData({...formData, targetPlan: e.target.value})}
                    >
                      {application.planCodes.map((planCode) => (
                        <option key={planCode} value={planCode}>{planCode}</option>
                      ))}
                    </select>
                  </div>
                  <div className="space-y-1.5">
                    <label className="text-sm font-medium text-foreground">체험 기간 (일)</label>
                    <Input
                      type="number" min={1}
                      value={formData.trialDays}
                      onChange={(e) => setFormData({...formData, trialDays: e.target.value})}
                    />
                  </div>
                </>
              )}

              {isDiscountType && (
                <div className="space-y-1.5">
                  <label className="text-sm font-medium text-foreground">
                    {formData.couponType === 'DISCOUNT_PERCENT' ? '할인율 (%)' : '할인 금액 (원)'} <span className="text-destructive">*</span>
                  </label>
                  <Input
                    type="number"
                    min={1}
                    value={formData.discountValue}
                    onChange={(e) => setFormData({...formData, discountValue: e.target.value})}
                    required
                  />
                </div>
              )}

              <div className="space-y-1.5">
                <label className="text-sm font-medium text-foreground">최대 사용 횟수</label>
                <Input
                  type="number" min={1}
                  value={formData.maxUses}
                  onChange={(e) => setFormData({...formData, maxUses: e.target.value})}
                />
              </div>

              <div className="space-y-1.5">
                <label className="text-sm font-medium text-foreground">유효 기간 (일)</label>
                <Input
                  type="number" min={1}
                  value={formData.validityDays}
                  onChange={(e) => setFormData({...formData, validityDays: e.target.value})}
                />
              </div>

              <Button
                type="submit"
                disabled={loading || !application.capabilities.couponRedemption}
                className="w-full"
              >
                {loading && <span className="mr-2">...</span>}
                쿠폰 발급하기
              </Button>
            </form>
          </CardContent>
        </Card>

        {issuedCoupon && (
          <Card className="border-2 border-primary h-fit">
            <CardContent className="p-6">
              <div className="flex flex-col h-full">
                <div className="flex items-center gap-2 mb-6">
                  <div className="bg-primary/10 text-primary p-2 rounded-full">
                    <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                    </svg>
                  </div>
                  <h3 className="text-xl font-bold text-primary">발급 완료</h3>
                </div>
                <div className="bg-muted/50 rounded-lg p-4 border border-border mt-auto">
                  <label className="text-xs text-muted-foreground font-medium">쿠폰 코드</label>
                  <div className="flex items-center justify-between mt-1 gap-3">
                    <div className="text-2xl font-mono font-bold tracking-wider text-foreground bg-background px-3 py-2 rounded border border-border shadow-sm flex-1 select-all break-all overflow-hidden">
                      {issuedCoupon.code}
                    </div>
                    <Button size="sm" onClick={() => copyToClipboard(issuedCoupon.code)}>
                      복사
                    </Button>
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>
        )}
      </div>

      {/* 쿠폰 목록 */}
      <Card>
        <CardContent className="p-4 sm:p-6">
          <div className="mb-4 flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
            <h3 className="text-xl font-bold">발급된 쿠폰 목록</h3>
            <Button size="sm" variant="outline" onClick={loadCoupons}>새로고침</Button>
          </div>
          {coupons.length === 0 ? (
            <p className="text-muted-foreground text-center py-8">발급된 쿠폰이 없습니다.</p>
          ) : (
            <Table mobileCards>
                <TableHeader>
                  <TableRow>
                    <TableHead>코드</TableHead>
                    <TableHead>유형</TableHead>
                    <TableHead>상태</TableHead>
                    <TableHead>사용</TableHead>
                    <TableHead>사용자</TableHead>
                    <TableHead>값</TableHead>
                    <TableHead>만료일</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {coupons.map((c) => (
                    <TableRow key={c.id}>
                      <TableCell className="font-mono font-bold">{c.code}</TableCell>
                      <TableCell>{typeLabel(c.type)}</TableCell>
                      <TableCell>
                        <Badge variant={statusColor(c.status) as any}>{c.status}</Badge>
                      </TableCell>
                      <TableCell>{c.currentUses}/{c.maxUses}</TableCell>
                      <TableCell className="min-w-[220px] align-top">{renderUsagePreview(c)}</TableCell>
                      <TableCell>
                        {c.type === 'TRIAL_PLAN' && `${c.trialDays}일 (${c.targetPlan})`}
                        {c.type === 'DISCOUNT_AMOUNT' && `${c.discountValue?.toLocaleString()}원`}
                        {c.type === 'DISCOUNT_PERCENT' && `${c.discountValue}%`}
                      </TableCell>
                      <TableCell className="text-muted-foreground">
                        {c.expiresAt ? new Date(c.expiresAt).toLocaleDateString() : '-'}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default CouponPage;
